import { useState, useEffect, useCallback } from 'react';
import { Link, router } from '@inertiajs/react';
import type { HexBuilding, AvailableBuildingType, BuildingsIndexResponse, AvailableBuildingsResponse } from '@/types/buildings';
import { formatDuration } from '@/types/buildings';
import { StarRating } from '@/components/Buildings/StarRating';
import { BuildingProgressBar } from '@/components/Buildings/BuildingProgressBar';

interface BuildingsTabProps {
    hexId: number;
    isOwner: boolean;
}

export function BuildingsTab({ hexId, isOwner }: BuildingsTabProps) {
    const [buildings, setBuildings] = useState<HexBuilding[]>([]);
    const [buildingUnderConstruction, setBuildingUnderConstruction] = useState<HexBuilding | null>(null);
    const [availableTypes, setAvailableTypes] = useState<AvailableBuildingType[]>([]);
    const [canBuild, setCanBuild] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isBuilding, setIsBuilding] = useState(false);
    const [showAvailable, setShowAvailable] = useState(false);

    // Fetch buildings data
    const fetchBuildings = useCallback(async () => {
        try {
            const response = await fetch(`/hex/${hexId}/buildings`);
            const data: BuildingsIndexResponse = await response.json();
            const underConstruction = data.buildings.find(b => b.level === 0) || null;
            const completed = data.buildings.filter(b => b.level >= 1);
            setBuildingUnderConstruction(underConstruction);
            setBuildings(completed);
            setCanBuild(data.canBuild);
        } catch (err) {
            console.error('Failed to fetch buildings:', err);
        } finally {
            setIsLoading(false);
        }
    }, [hexId]);

    // Fetch available building types
    const fetchAvailable = useCallback(async () => {
        try {
            const response = await fetch(`/hex/${hexId}/buildings/available`);
            const data: AvailableBuildingsResponse = await response.json();
            setAvailableTypes(data.availableTypes);
        } catch (err) {
            console.error('Failed to fetch available buildings:', err);
        }
    }, [hexId]);

    useEffect(() => {
        fetchBuildings();
        fetchAvailable();
    }, [fetchBuildings, fetchAvailable]);

    const handleBuild = (buildingTypeId: number) => {
        setIsBuilding(true);
        router.post(
            `/hex/${hexId}/buildings`,
            { building_type_id: buildingTypeId },
            {
                preserveScroll: true,
                onSuccess: () => {
                    fetchBuildings();
                    fetchAvailable();
                    setShowAvailable(false);
                },
                onFinish: () => setIsBuilding(false),
            }
        );
    };

    const handleCancel = (buildingId: number) => {
        router.delete(`/hex/${hexId}/buildings/${buildingId}`, {
            preserveScroll: true,
            onSuccess: () => {
                fetchBuildings();
                fetchAvailable();
            },
        });
    };

    if (isLoading) {
        return (
            <div className="text-center text-amber-200/60 py-8 font-serif italic">
                Loading blueprints...
            </div>
        );
    }

    return (
        <div className="max-w-5xl">
            {/* Header */}
            <div className="text-center mb-8">
                <h2 className="text-3xl font-serif font-bold text-amber-100 tracking-wide drop-shadow-lg">
                    ⚒ Construction Office ⚒
                </h2>
                <p className="text-amber-200/60 text-sm mt-1 font-serif italic">
                    Blueprints and building permits
                </p>
            </div>

            {/* Desk Surface */}
            <div
                className="rounded-lg p-8 relative"
                style={{
                    background: 'linear-gradient(180deg, #5c4033 0%, #4a3429 50%, #3d2a1f 100%)',
                    boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.5), 0 4px 20px rgba(0,0,0,0.3)',
                    border: '4px solid #3d2a1f',
                }}
            >
                {/* Wood grain texture */}
                <div
                    className="absolute inset-0 rounded-lg pointer-events-none opacity-10"
                    style={{
                        backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(0,0,0,0.1) 50px, rgba(0,0,0,0.1) 100px)',
                    }}
                />

                <div className="relative z-10 space-y-8">
                    {/* Build New Section - Owner Only */}
                    {isOwner && (
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-xl font-serif font-semibold text-amber-100">
                                    📐 Available Blueprints
                                </h3>
                                {!showAvailable && canBuild && !buildingUnderConstruction && (
                                    <button
                                        onClick={() => setShowAvailable(true)}
                                        className="px-4 py-2 bg-amber-700 hover:bg-amber-600 text-amber-100 rounded border-2 border-amber-600 transition-colors font-serif"
                                    >
                                        View Blueprints
                                    </button>
                                )}
                                {showAvailable && (
                                    <button
                                        onClick={() => setShowAvailable(false)}
                                        className="px-4 py-2 bg-amber-900/50 hover:bg-amber-800/50 text-amber-200 rounded border border-amber-700 transition-colors font-serif"
                                    >
                                        Hide Blueprints
                                    </button>
                                )}
                            </div>

                            {/* Building Under Construction */}
                            {buildingUnderConstruction && (
                                <div className="mb-6">
                                    <BuildingUnderConstructionCard
                                        building={buildingUnderConstruction}
                                        onCancel={() => handleCancel(buildingUnderConstruction.id)}
                                        onComplete={fetchBuildings}
                                    />
                                </div>
                            )}

                            {/* Cannot build message */}
                            {!canBuild && !showAvailable && !buildingUnderConstruction && (
                                <p className="text-amber-200/50 font-serif italic text-sm">
                                    No blueprints available. Complete prerequisite buildings first.
                                </p>
                            )}

                            {/* Available Blueprints */}
                            {showAvailable && (
                                <div className="grid gap-6 sm:grid-cols-2">
                                    {availableTypes.length === 0 ? (
                                        <p className="text-amber-200/50 font-serif italic col-span-full">
                                            No blueprints available.
                                        </p>
                                    ) : (
                                        availableTypes.map((type) => (
                                            <BlueprintCard
                                                key={type.id}
                                                buildingType={type}
                                                onBuild={() => handleBuild(type.id)}
                                                isBuilding={isBuilding}
                                                rotation={getBlueprintRotation(type.id)}
                                            />
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Completed Buildings - Envelope Style */}
                    <div>
                        <h3 className="text-xl font-serif font-semibold text-amber-100 mb-4">
                            📋 Completed Structures
                        </h3>

                        {buildings.length === 0 ? (
                            <p className="text-amber-200/50 font-serif italic">
                                No completed buildings on this hex.
                            </p>
                        ) : (
                            <div className="grid gap-6 sm:grid-cols-2">
                                {buildings.map((building, index) => (
                                    <BuildingEnvelope
                                        key={building.id}
                                        building={building}
                                        rotation={getEnvelopeRotation(building.id)}
                                        onComplete={fetchBuildings}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Get a pseudo-random rotation for envelope based on ID
 */
function getEnvelopeRotation(id: number): number {
    const seed = (id * 13) % 19;
    return (seed - 9) * 0.25;
}

/**
 * Get a pseudo-random rotation for blueprint based on ID
 */
function getBlueprintRotation(id: number): number {
    const seed = (id * 17) % 23;
    return (seed - 11) * 0.3;
}

/**
 * BlueprintCard - Paper-like blueprint for available buildings
 */
function BlueprintCard({
    buildingType,
    onBuild,
    isBuilding,
    rotation,
}: {
    buildingType: AvailableBuildingType;
    onBuild: () => void;
    isBuilding: boolean;
    rotation: number;
}) {
    const { name, rank, description, baseCost, buildTimeMinutes, requirements, canAfford } = buildingType;
    const canBuild = canAfford && !isBuilding;

    return (
        <div
            className="relative group"
            style={{
                transform: `rotate(${rotation}deg)`,
                transition: 'transform 0.3s ease',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'rotate(0deg) translateY(-4px)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = `rotate(${rotation}deg)`;
            }}
        >
            {/* Pin/Tack at top */}
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-20">
                <div
                    className="w-4 h-4 rounded-full shadow-lg"
                    style={{
                        background: 'radial-gradient(circle at 30% 30%, #cd853f 0%, #8b4513 50%, #654321 100%)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.5), inset 0 1px 2px rgba(255,255,255,0.3)',
                    }}
                />
            </div>

            {/* Blue-tinted Paper */}
            <div
                className="relative rounded-sm overflow-hidden"
                style={{
                    background: 'linear-gradient(135deg, #d4e4f0 0%, #c5d8e8 50%, #d8e8f2 100%)',
                    boxShadow: '4px 4px 12px rgba(0,0,0,0.3), -1px -1px 4px rgba(0,0,0,0.1)',
                    border: '1px solid #8bb0cc',
                }}
            >
                {/* Blueprint grid pattern */}
                <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        backgroundImage: `
                            linear-gradient(rgba(70,130,180,0.25) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(70,130,180,0.25) 1px, transparent 1px)
                        `,
                        backgroundSize: '20px 20px',
                    }}
                />

                {/* Subtle fold/crease effect */}
                <div
                    className="absolute top-0 right-0 w-20 h-20 pointer-events-none"
                    style={{
                        background: 'linear-gradient(135deg, transparent 50%, rgba(70,130,180,0.08) 100%)',
                    }}
                />

                {/* Content */}
                <div className="relative p-5">
                    {/* Header */}
                    <div className="text-center mb-4 border-b-2 border-blue-900/40 pb-3">
                        <div className="text-sm font-mono text-blue-900 uppercase tracking-[0.3em] font-bold">
                            Architectural Plan
                        </div>
                    </div>

                    {/* Building Name */}
                    <h4 className="font-mono font-bold text-blue-900 text-xl text-center mb-4 uppercase tracking-wide">
                        {name}
                    </h4>

                    {/* Specifications Table */}
                    <div className="space-y-2 mb-4 font-mono text-sm text-blue-900">
                        {/* Rank */}
                        <div className="flex items-end">
                            <span className="uppercase tracking-wide">Rank</span>
                            <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                            <span className="font-bold">{rank}</span>
                        </div>

                        {/* Construction Time */}
                        <div className="flex items-end">
                            <span className="uppercase tracking-wide">Est. Time</span>
                            <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                            <span className="font-bold">{formatDuration(buildTimeMinutes)}</span>
                        </div>

                        {/* Requirements */}
                        {requirements.length > 0 && (
                            <div className="flex items-end">
                                <span className="uppercase tracking-wide">Requires</span>
                                <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                <span className="font-bold">{requirements.join(', ')}</span>
                            </div>
                        )}
                    </div>

                    {/* Description */}
                    {description && (
                        <p className="text-blue-900/80 text-sm font-mono mb-4 text-center border-t border-b border-blue-900/20 py-2">
                            {description}
                        </p>
                    )}

                    {/* Materials Section */}
                    <div className="mb-4">
                        <div className="text-sm font-mono text-blue-900 uppercase tracking-wide font-bold mb-2 border-b border-blue-900/40 pb-1">
                            Materials Required
                        </div>
                        <div className="space-y-1 font-mono text-sm text-blue-900">
                            {baseCost.tea > 0 && (
                                <div className="flex items-end">
                                    <span>Tea</span>
                                    <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                    <span className="font-bold">{baseCost.tea}</span>
                                </div>
                            )}
                            {baseCost.wood > 0 && (
                                <div className="flex items-end">
                                    <span>Wood</span>
                                    <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                    <span className="font-bold">{baseCost.wood}</span>
                                </div>
                            )}
                            {baseCost.mystical > 0 && (
                                <div className="flex items-end">
                                    <span>Mystical</span>
                                    <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                    <span className="font-bold">{baseCost.mystical}</span>
                                </div>
                            )}
                            {baseCost.medicinal > 0 && (
                                <div className="flex items-end">
                                    <span>Medicinal</span>
                                    <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                    <span className="font-bold">{baseCost.medicinal}</span>
                                </div>
                            )}
                            {baseCost.edible > 0 && (
                                <div className="flex items-end">
                                    <span>Edible</span>
                                    <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                    <span className="font-bold">{baseCost.edible}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Build Stamp Button */}
                    <div
                        className={`relative ${!canBuild ? 'opacity-50' : 'cursor-pointer'}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (canBuild) onBuild();
                        }}
                    >
                        <div
                            className={`relative p-3 rounded-sm border-4 border-dashed text-center transition-all duration-200 ${
                                canBuild ? 'hover:translate-y-0.5 hover:translate-x-0.5' : ''
                            }`}
                            style={{
                                borderColor: canBuild ? 'rgba(22, 101, 52, 0.5)' : 'rgba(185, 28, 28, 0.4)',
                                background: canBuild
                                    ? 'linear-gradient(135deg, rgba(22, 101, 52, 0.1) 0%, rgba(22, 101, 52, 0.15) 100%)'
                                    : 'linear-gradient(135deg, rgba(185, 28, 28, 0.08) 0%, rgba(185, 28, 28, 0.12) 100%)',
                            }}
                        >
                            {/* Inner ring */}
                            <div className={`absolute inset-1.5 rounded-sm border-2 ${
                                canBuild ? 'border-green-800/30' : 'border-red-800/20'
                            }`} />

                            <div className={`relative text-sm font-serif font-bold uppercase tracking-wider ${
                                canBuild ? 'text-green-800' : 'text-red-800'
                            }`}>
                                {isBuilding ? 'Approving...' : canAfford ? '⚒ Approve Construction' : '✗ Cannot Afford'}
                            </div>
                        </div>

                        {/* Stamp shadow on hover */}
                        {canBuild && (
                            <div className="absolute inset-0 rounded-sm bg-green-900/15 opacity-0 hover:opacity-100 transition-opacity -z-10 translate-y-1 translate-x-1" />
                        )}
                    </div>
                </div>

                {/* Paper texture */}
                <div
                    className="absolute inset-0 pointer-events-none opacity-25"
                    style={{
                        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.4\'/%3E%3C/svg%3E")',
                    }}
                />
            </div>

            {/* Hover lift shadow */}
            <div
                className="absolute inset-0 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none -z-10"
                style={{
                    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                    transform: 'translateY(4px)',
                }}
            />
        </div>
    );
}

/**
 * Format a date for display
 */
function formatCompletionTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();

    if (diffMs <= 0) return 'Complete!';

    const diffMins = Math.ceil(diffMs / 60000);
    if (diffMins < 60) return `~${diffMins} min`;

    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;
    if (diffHours < 24) {
        return remainingMins > 0 ? `~${diffHours}h ${remainingMins}m` : `~${diffHours}h`;
    }

    const diffDays = Math.floor(diffHours / 24);
    const remainingHours = diffHours % 24;
    return remainingHours > 0 ? `~${diffDays}d ${remainingHours}h` : `~${diffDays}d`;
}

/**
 * BuildingUnderConstructionCard - Blueprint rolled out on desk
 */
function BuildingUnderConstructionCard({
    building,
    onCancel,
    onComplete,
}: {
    building: HexBuilding;
    onCancel: () => void;
    onComplete: () => void;
}) {
    const [progress, setProgress] = useState(0);
    const [timeRemaining, setTimeRemaining] = useState('');

    // Calculate and update progress
    useEffect(() => {
        if (!building.upgradeStartedAt || !building.upgradeCompletesAt) return;

        const startTime = new Date(building.upgradeStartedAt).getTime();
        const endTime = new Date(building.upgradeCompletesAt).getTime();
        const totalDuration = endTime - startTime;

        const updateProgress = () => {
            const now = Date.now();
            const elapsed = now - startTime;
            const currentProgress = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
            setProgress(Math.round(currentProgress));
            setTimeRemaining(formatCompletionTime(building.upgradeCompletesAt!));

            if (currentProgress >= 100) {
                onComplete();
            }
        };

        updateProgress();
        const interval = setInterval(updateProgress, 1000);

        return () => clearInterval(interval);
    }, [building.upgradeStartedAt, building.upgradeCompletesAt, onComplete]);

    return (
        <div className="relative max-w-2xl mx-auto">
            {/* Rolled edge shadow on left */}
            <div
                className="absolute left-0 top-2 bottom-2 w-4 -ml-2 rounded-l-full pointer-events-none z-20"
                style={{
                    background: 'linear-gradient(90deg, rgba(0,0,0,0.3) 0%, transparent 100%)',
                }}
            />

            {/* Rolled edge shadow on right */}
            <div
                className="absolute right-0 top-2 bottom-2 w-4 -mr-2 rounded-r-full pointer-events-none z-20"
                style={{
                    background: 'linear-gradient(-90deg, rgba(0,0,0,0.3) 0%, transparent 100%)',
                }}
            />

            {/* Blueprint Paper - Rolled Out */}
            <div
                className="relative rounded-sm overflow-hidden"
                style={{
                    background: 'linear-gradient(135deg, #d4e4f0 0%, #c5d8e8 50%, #d8e8f2 100%)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.4), inset 0 0 30px rgba(70,130,180,0.1)',
                    border: '1px solid #8bb0cc',
                }}
            >
                {/* Blueprint grid pattern */}
                <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        backgroundImage: `
                            linear-gradient(rgba(70,130,180,0.25) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(70,130,180,0.25) 1px, transparent 1px)
                        `,
                        backgroundSize: '20px 20px',
                    }}
                />

                {/* Fold creases to show it's rolled out */}
                <div
                    className="absolute left-8 top-0 bottom-0 w-px pointer-events-none"
                    style={{
                        background: 'linear-gradient(180deg, transparent 0%, rgba(70,130,180,0.15) 30%, rgba(70,130,180,0.15) 70%, transparent 100%)',
                    }}
                />
                <div
                    className="absolute right-8 top-0 bottom-0 w-px pointer-events-none"
                    style={{
                        background: 'linear-gradient(180deg, transparent 0%, rgba(70,130,180,0.15) 30%, rgba(70,130,180,0.15) 70%, transparent 100%)',
                    }}
                />

                {/* Content */}
                <div className="relative p-6">
                    {/* Header */}
                    <div className="text-center mb-5 border-b-2 border-blue-900/40 pb-3">
                        <div className="text-sm font-mono text-blue-900 uppercase tracking-[0.3em] font-bold mb-1">
                            Construction In Progress
                        </div>
                        <h3 className="text-2xl font-mono font-bold text-blue-900 uppercase tracking-wide">
                            {building.buildingType.name}
                        </h3>
                    </div>

                    {/* Progress Section - Handwritten Style */}
                    <div className="flex items-start justify-between gap-8 mb-6">
                        {/* Left: Handwritten Progress */}
                        <div className="flex-1">
                            <div className="text-xs font-mono text-blue-900/70 uppercase tracking-wide mb-2 border-b border-blue-900/30 pb-1">
                                Completion Status
                            </div>

                            {/* Large handwritten percentage */}
                            <div className="relative">
                                <div
                                    className="text-5xl font-bold text-blue-800 mb-2"
                                    style={{
                                        fontFamily: "'Brush Script MT', 'Segoe Script', 'Bradley Hand', cursive",
                                        textShadow: '1px 1px 0 rgba(70,130,180,0.2)',
                                    }}
                                >
                                    {progress}%
                                </div>

                                {/* Underline flourish */}
                                <svg
                                    className="absolute -bottom-1 left-0 w-24 h-3 text-blue-800/40"
                                    viewBox="0 0 100 12"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                >
                                    <path d="M0 8 Q 30 2, 60 8 T 100 6" />
                                </svg>
                            </div>

                            {/* Progress bar visualization */}
                            <div className="mt-4 relative">
                                <div className="h-3 rounded-full bg-blue-200/50 border border-blue-900/20 overflow-hidden">
                                    <div
                                        className="h-full transition-all duration-1000 ease-out rounded-full"
                                        style={{
                                            width: `${progress}%`,
                                            background: 'linear-gradient(90deg, rgba(30,64,175,0.6) 0%, rgba(59,130,246,0.8) 100%)',
                                        }}
                                    />
                                </div>
                                {/* Blueprint-style measurement marks */}
                                <div className="flex justify-between mt-1 font-mono text-[10px] text-blue-900/50">
                                    <span>0</span>
                                    <span>25</span>
                                    <span>50</span>
                                    <span>75</span>
                                    <span>100</span>
                                </div>
                            </div>
                        </div>

                        {/* Right: Typewriter Details */}
                        <div className="flex-1">
                            <div className="space-y-3 font-mono text-sm text-blue-900">
                                <div className="text-xs uppercase tracking-wide text-blue-900/70 border-b border-blue-900/30 pb-1">
                                    Project Details
                                </div>

                                <div className="flex items-end">
                                    <span className="uppercase tracking-wide">Est. Remaining</span>
                                    <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                    <span
                                        className="font-bold text-blue-800"
                                        style={{
                                            fontFamily: "'Brush Script MT', 'Segoe Script', 'Bradley Hand', cursive",
                                            fontSize: '1.1rem',
                                        }}
                                    >
                                        {timeRemaining}
                                    </span>
                                </div>

                                <div className="flex items-end">
                                    <span className="uppercase tracking-wide">Status</span>
                                    <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                    <span
                                        className="font-bold text-blue-800"
                                        style={{
                                            fontFamily: "'Brush Script MT', 'Segoe Script', 'Bradley Hand', cursive",
                                            fontSize: '1.1rem',
                                        }}
                                    >
                                        Active
                                    </span>
                                </div>

                                <div className="flex items-end">
                                    <span className="uppercase tracking-wide">Rank</span>
                                    <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                    <span className="font-bold">{building.buildingType.rank}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Cancel Stamp Button */}
                    <div className="flex justify-center">
                        <button
                            onClick={onCancel}
                            className="relative group"
                        >
                            <div
                                className="relative px-6 py-3 rounded-sm border-4 border-dashed text-center transition-all duration-200 hover:translate-y-0.5 hover:translate-x-0.5"
                                style={{
                                    borderColor: 'rgba(185, 28, 28, 0.5)',
                                    background: 'linear-gradient(135deg, rgba(185, 28, 28, 0.1) 0%, rgba(185, 28, 28, 0.15) 100%)',
                                }}
                            >
                                {/* Inner ring */}
                                <div className="absolute inset-1.5 rounded-sm border-2 border-red-800/30" />

                                <div className="relative text-sm font-serif font-bold uppercase tracking-wider text-red-800">
                                    ✗ Cancel Construction
                                </div>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Paper texture */}
                <div
                    className="absolute inset-0 pointer-events-none opacity-25"
                    style={{
                        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.4\'/%3E%3C/svg%3E")',
                    }}
                />
            </div>

            {/* Desk shadow under rolled blueprint */}
            <div
                className="absolute -bottom-2 left-4 right-4 h-4 rounded-full pointer-events-none"
                style={{
                    background: 'radial-gradient(ellipse, rgba(0,0,0,0.3) 0%, transparent 70%)',
                }}
            />
        </div>
    );
}

/**
 * BuildingEnvelope - Completed building styled as an envelope
 */
function BuildingEnvelope({
    building,
    rotation,
    onComplete,
}: {
    building: HexBuilding;
    rotation: number;
    onComplete: () => void;
}) {
    const slotsUsed = building.assignedCharacterCount ?? 0;
    const totalSlots = building.characterSlotCount ?? building.stars;
    const assignedNames = building.assignedCharacters?.map(c => c.name).join(', ');

    // Status stamp info
    const getStatusStamp = () => {
        if (building.isUpgrading) {
            return { text: 'UPGRADING', color: 'rgba(234, 179, 8, 0.8)', rotation: -8 };
        }
        return { text: 'OPERATIONAL', color: 'rgba(34, 197, 94, 0.8)', rotation: -5 };
    };

    const statusStamp = getStatusStamp();

    return (
        <Link
            href={`/hex/${building.hexId}/buildings/${building.id}`}
            className="block relative group"
            style={{
                transform: `rotate(${rotation}deg)`,
                transition: 'transform 0.3s ease',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'rotate(0deg) translateY(-4px)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = `rotate(${rotation}deg)`;
            }}
        >
            {/* Envelope */}
            <div
                className="relative overflow-hidden rounded-sm"
                style={{
                    background: 'linear-gradient(180deg, #f5edd6 0%, #e8dcc4 30%, #d9cbb0 100%)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3), inset 0 -1px 0 rgba(0,0,0,0.1)',
                    border: '1px solid #c9b896',
                }}
            >
                {/* Envelope flap */}
                <div
                    className="absolute top-0 left-0 right-0 h-12 pointer-events-none"
                    style={{
                        background: 'linear-gradient(180deg, #ede4d0 0%, #e0d4be 100%)',
                        clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
                        borderBottom: '1px solid #c9b896',
                    }}
                />

                {/* Flap shadow */}
                <div
                    className="absolute top-10 left-0 right-0 h-4 pointer-events-none"
                    style={{
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.08) 0%, transparent 100%)',
                    }}
                />

                {/* Wax Seal - top right */}
                <div
                    className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full flex items-center justify-center"
                    style={{
                        background: `radial-gradient(circle at 30% 30%, #a0522d 0%, #8b4513 50%, #654321 100%)`,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.4), inset 0 1px 2px rgba(255,255,255,0.2)',
                    }}
                >
                    <span className="text-amber-100 text-sm">🏠</span>
                </div>

                {/* Content */}
                <div className="pt-14 pb-5 px-5 relative">
                    {/* Decorative emblem */}
                    <div className="absolute left-4 top-14 opacity-10 pointer-events-none">
                        <div className="text-5xl text-amber-900">⚜</div>
                    </div>

                    {/* Building Name */}
                    <div className="text-center mb-3 relative z-10">
                        <div className="text-xs font-serif text-amber-800/50 uppercase tracking-widest mb-1">
                            Building Permit
                        </div>
                        <h3 className="text-lg font-serif font-bold text-amber-900 group-hover:text-amber-700 transition-colors">
                            {building.buildingType.name}
                        </h3>
                        <div className="h-px w-24 mx-auto mt-2 bg-amber-800/30" />
                    </div>

                    {/* Details */}
                    <div className="flex items-center justify-center gap-4 text-sm font-serif text-amber-800/70 mb-3">
                        <span>Level {building.level}</span>
                        <span className="text-amber-800/30">•</span>
                        <StarRating stars={building.stars} size="sm" />
                    </div>

                    {/* Assigned Characters */}
                    <div className="text-center text-xs font-serif text-amber-800/60 mb-3">
                        {slotsUsed > 0 ? (
                            <span className="text-green-700">{assignedNames} ({slotsUsed}/{totalSlots})</span>
                        ) : (
                            <span>No characters assigned ({slotsUsed}/{totalSlots})</span>
                        )}
                    </div>

                    {/* Upgrade Progress */}
                    {building.isUpgrading && building.upgradeStartedAt && building.upgradeCompletesAt && (
                        <div className="mb-3">
                            <BuildingProgressBar
                                startedAt={building.upgradeStartedAt}
                                completesAt={building.upgradeCompletesAt}
                                label={building.isUpgradingLevel ? `Upgrading to Lvl ${building.level + 1}` : `Upgrading to ★${building.stars + 1}`}
                                onComplete={onComplete}
                            />
                        </div>
                    )}

                    {/* Status Stamp */}
                    <div
                        className="absolute bottom-3 right-4"
                        style={{ transform: `rotate(${statusStamp.rotation}deg)` }}
                    >
                        <div
                            className="px-2 py-1 rounded-sm border-2 border-dashed"
                            style={{
                                borderColor: statusStamp.color,
                                background: `linear-gradient(135deg, ${statusStamp.color.replace('0.8', '0.1')} 0%, ${statusStamp.color.replace('0.8', '0.15')} 100%)`,
                            }}
                        >
                            <div
                                className="text-[10px] font-serif font-bold uppercase tracking-wider"
                                style={{ color: statusStamp.color.replace('0.8', '1') }}
                            >
                                {statusStamp.text}
                            </div>
                        </div>
                    </div>

                    {/* Click hint */}
                    <div className="text-center">
                        <span className="text-xs font-serif text-amber-800/40 italic">
                            View details →
                        </span>
                    </div>
                </div>

                {/* Paper texture */}
                <div
                    className="absolute inset-0 pointer-events-none opacity-20"
                    style={{
                        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.8\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.3\'/%3E%3C/svg%3E")',
                    }}
                />
            </div>

            {/* Hover lift shadow */}
            <div
                className="absolute inset-0 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none -z-10"
                style={{
                    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                    transform: 'translateY(4px)',
                }}
            />
        </Link>
    );
}
